/**
 * useLoungeChat Requests - Consent-First Chat Request System
 * 
 * Purpose:
 * - Allow users to request 1:1 chat with Lounge participants
 * - Poll for incoming requests
 * - Handle accept/decline responses
 * 
 * Design:
 * - Polling every 20s for incoming requests (will add Ably realtime later)
 * - Clear UI feedback for both sender and receiver
 * - Graceful error handling
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from "../api/client";
import { toast } from 'sonner';


export function useLoungeChatRequests({ token, enabled = true }) {
  const [incomingRequest, setIncomingRequest] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isResponding, setIsResponding] = useState(false);

  /**
   * Poll for incoming chat requests
   */
  const checkIncoming = useCallback(async () => {
    if (!token || !enabled) return;

    try {
      const res = await apiClient.get(`/lounge/chat-requests/incoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIncomingRequest(res.data);
    } catch (err) {
      // Silently fail - this is a background poll
      console.warn('[useLoungeChatRequests] Failed to check incoming:', err);
    }
  }, [token, enabled]);

  /**
   * Send a chat request to another user
   */
  const sendRequest = useCallback(async (toUserId) => {
    if (!token || isSending) return;

    setIsSending(true);
    try {
      const res = await apiClient.post(
        `/lounge/chat-requests`,
        { to_user_id: toUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Chat request sent - give them a moment to respond.');
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || 'Could not send chat request.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsSending(false);
    }
  }, [token, isSending]);

  /**
   * Respond to an incoming chat request
   */
  const respondToRequest = useCallback(async (requestId, action) => {
    if (!token || isResponding) return;

    setIsResponding(true);
    try {
      const res = await apiClient.post(
        `/lounge/chat-requests/${requestId}/respond`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (action === 'accept') {
        toast.success('They\'re down to chat. Opening your messages...');
      } else {
        toast.success('No problem. Keep enjoying the Lounge.');
      }

      // Clear the incoming request
      setIncomingRequest(null);

      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || 'That request is no longer active.';
      toast.error(message);
      
      // Clear the request on error (probably expired)
      setIncomingRequest(null);
      
      return { success: false, error: message };
    } finally {
      setIsResponding(false);
    }
  }, [token, isResponding]);

  /**
   * Poll for incoming requests while enabled
   */
  useEffect(() => {
    if (!enabled || !token) return;

    // Check immediately
    checkIncoming();

    // Then poll every 20 seconds
    const interval = setInterval(checkIncoming, 20000);

    return () => clearInterval(interval);
  }, [enabled, token, checkIncoming]);

  return {
    // State
    incomingRequest,
    isSending,
    isResponding,
    
    // Actions
    sendRequest,
    respondToRequest,
    refresh: checkIncoming,
  };
}
